import React, { useState, useRef, useEffect } from "react";
import {
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Container,
  Grid,
  Paper,
  Stack,
  IconButton,
  SelectChangeEvent,
  Chip,
} from "@mui/material";
import {
  CloudUpload as CloudUploadIcon,
  Build as BuildIcon,
  Download as DownloadIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  RemoveCircleOutline as RemoveCircleOutlineIcon,
  Undo as UndoIcon,
  RestartAlt as RestartAltIcon,
  Clear as ClearIcon,
  DragIndicator as DragIndicatorIcon,
  ContentCopy as ContentCopyIcon,
  AutoAwesome as AutoAwesomeIcon,
  RocketLaunch as RocketLaunchIcon,
  TipsAndUpdates as TipsAndUpdatesIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
} from "@mui/icons-material";
import "./App.css";
import ControlsBar from "./components/ControlsBar";
import SEOHead from "./components/SEOHead";
import PrivacyPolicy from "./components/PrivacyPolicy";
import About from "./components/About";
import Blog from "./components/Blog";
import BlogPost from "./components/BlogPost";
import { API_ENDPOINTS } from "./config/api";
import { getPlatforms } from "./config/jobPlatforms";
import { useGeoLocation } from "./hooks/useGeoLocation";
import { useLanguage } from "./contexts/LanguageContext";
import { useTheme } from "./contexts/ThemeContext";
import { GlobalWorkerOptions, getDocument } from "pdfjs-dist/build/pdf";
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.entry";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "react-beautiful-dnd";
// Set workerSrc for pdfjs
GlobalWorkerOptions.workerSrc = pdfjsWorker;

// Define an interface for the resume data structure
interface ResumeData {
  name: string;
  contact: {
    email: string;
    phone: string;
    location: string;
    link?: string; // Optional website or portfolio URL
    behance?: string; // Optional fallback compatibility
  };
  summary: string;
  experience: Array<{
    id?: string; // Added ID for react-beautiful-dnd
    title: string;
    company: string;
    dates: string;
    details: string[];
  }>;
  education: Array<{
    id?: string; // Added ID for react-beautiful-dnd
    degree: string;
    institution: string;
    dates: string;
  }>;
  skills: Array<{ id: string; value: string }>; // Updated skills to object array for dnd
}

// Add interface for the removed item state
interface RemovedItemInfo {
  item: any;
  path: string; // Path to the ARRAY the item was removed from
  index: number; // Index within that array
}

// Helper to generate unique IDs for dnd
const generateId = () =>
  `id-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

const PDF_TEMPLATE_VALUES = [
  "classic",
  "creative",
  "compact",
  "executive",
  "blueprint",
];

function App() {
  const { t, isRTL } = useLanguage();
  const { mode } = useTheme();
  const geo = useGeoLocation();

  const getPage = () => {
    const hash = window.location.hash;
    if (hash === "#/privacy-policy") return "privacy";
    if (hash === "#/about") return "about";
    if (hash === "#/blog") return "blog";
    if (hash.startsWith("#/blog/")) return "blog-post";
    return "main";
  };

  const [currentPage, setCurrentPage] = React.useState(getPage);

  React.useEffect(() => {
    const handleHash = () => setCurrentPage(getPage());
    window.addEventListener("hashchange", handleHash);
    return () => window.removeEventListener("hashchange", handleHash);
  }, []);

  React.useEffect(() => { window.scrollTo(0, 0); }, []);

  const [resumeText, setResumeText] = useState(
    () => localStorage.getItem("resumeText") || "",
  );
  const [jobDescription, setJobDescription] = useState(
    () => localStorage.getItem("jobDescription") || "",
  );
  const [fileName, setFileName] = useState("");
  const [loading, setLoading] = useState(false);
  const [optimizeFloatingStatus, setOptimizeFloatingStatus] = useState<
    "idle" | "optimizing" | "done"
  >("idle");
  const [error, setError] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const optimizeDoneTimerRef = useRef<number | null>(null);
  const [optimizationStyle, setOptimizationStyle] = useState("Default");
  const [optimizedJson, setOptimizedJson] = useState<ResumeData | null>(null);
  const [originalOptimizedJson, setOriginalOptimizedJson] =
    useState<ResumeData | null>(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<string>(() => {
    const saved = localStorage.getItem("selectedTemplate");
    return saved && PDF_TEMPLATE_VALUES.includes(saved) ? saved : "classic";
  });

  const ACCENT_PALETTE = [
    "#ff8a2a", "#2d4a6e", "#1565c0", "#0d9488",
    "#2e7d32", "#6a1b9a", "#c62828", "#b5891c",
  ];
  const ACCENT_DEFAULTS: Record<string, string> = { creative: "#2d4a6e", executive: "#ff8a2a" };
  const templateSupportsAccent = ["creative", "executive"].includes(selectedTemplate);
  const [accentColor, setAccentColor] = useState<string>(
    ACCENT_DEFAULTS[selectedTemplate] || "#ff8a2a"
  );
  const shuffleAccent = () => {
    const others = ACCENT_PALETTE.filter(c => c !== accentColor);
    setAccentColor(others[Math.floor(Math.random() * others.length)]);
  };
  const [lastRemovedItem, setLastRemovedItem] =
    useState<RemovedItemInfo | null>(null);
  const [searchLinkTitle, setSearchLinkTitle] = useState<string>("");
  const [titleExtractionLoading, setTitleExtractionLoading] =
    useState<boolean>(false);
  const [titleExtractionError, setTitleExtractionError] = useState<string>("");

  useEffect(() => {
    return () => {
      if (optimizeDoneTimerRef.current) {
        window.clearTimeout(optimizeDoneTimerRef.current);
      }
    };
  }, []);

  // --- Helper: broaden a job title for search-link use only ---
  // Strips entry-level qualifiers (Intern, Trainee, etc.) and single-word
  // sub-specializations after "&" or "/" so search URLs return more results.
  // The original title is kept as-is in the field; only links use this.
  const broadenTitleForSearch = (title: string): string => {
    const broad = title
      .replace(/\b(intern|internee|trainee|apprentice|entry[- ]level|graduate)\b/gi, "")
      .replace(/\s*[&\/]\s*\w+$/i, "") // strip trailing "& Word" or "/ Word"
      .replace(/\s{2,}/g, " ")
      .trim();
    return broad || title; // fallback to original if result somehow empties
  };

  // --- Helper: get job platform links for detected country ---
  const getJobLinks = (title: string) =>
    geo.loading || !title.trim() ? [] : getPlatforms(geo.countryCode).map((p) => ({
      name: p.name,
      url: p.getUrl(broadenTitleForSearch(title)),
    }));

  // PDF Template Selection
  const availableTemplates = PDF_TEMPLATE_VALUES.map((value) => ({ value }));

  useEffect(() => {
    localStorage.setItem("resumeText", resumeText);
  }, [resumeText]);

  useEffect(() => {
    localStorage.setItem("jobDescription", jobDescription);
  }, [jobDescription]);

  useEffect(() => {
    localStorage.setItem("selectedTemplate", selectedTemplate);
    if (ACCENT_DEFAULTS[selectedTemplate]) setAccentColor(ACCENT_DEFAULTS[selectedTemplate]);
  }, [selectedTemplate]);

  // Sync AI-detected profession title to experience data for PDF generation
  useEffect(() => {
    if (
      searchLinkTitle &&
      optimizedJson &&
      optimizedJson.experience &&
      optimizedJson.experience.length > 0
    ) {
      const updated = JSON.parse(JSON.stringify(optimizedJson));
      if (updated.experience[0].title !== searchLinkTitle) {
        updated.experience[0].title = searchLinkTitle;
        setOptimizedJson(updated);
      }
    }
  }, [searchLinkTitle, optimizedJson]);

  // --- useEffect for AI Title Extraction ---
  useEffect(() => {
    const extractTitleWithAI = async () => {
      if (!resumeText || resumeText.trim().length < 50) {
        // Basic check: don't run on empty or very short text
        setSearchLinkTitle(""); // Clear title if resume text is cleared/too short
        return;
      }

      setTitleExtractionLoading(true);
      setTitleExtractionError("");
      console.log(
        "[Title Extraction] Calling backend API /api/extract-title...",
      );

      try {
        const response = await fetch(API_ENDPOINTS.extractTitle, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ resumeText }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || `API Error: ${response.status}`);
        }

        const data = await response.json();
        const newTitle = data.extractedTitle || "";
        console.log("[Title Extraction] Received title from AI:", newTitle);
        setSearchLinkTitle(newTitle);
      } catch (err: any) {
        console.error(
          "[Title Extraction] Error fetching title from backend:",
          err,
        );
        setTitleExtractionError(
          `${t("errors.failedToGetTitleSuggestion")}: ${err.message}`,
        );
        // Optionally clear the title on error, or leave the previous one?
        // setSearchLinkTitle('');
      } finally {
        setTitleExtractionLoading(false);
      }
    };

    // Debounce the API call slightly to avoid spamming while typing/pasting
    const debounceTimeout = setTimeout(() => {
      extractTitleWithAI();
    }, 500); // 500ms delay

    // Cleanup function to clear timeout if resumeText changes again quickly
    return () => clearTimeout(debounceTimeout);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeText]); // t is a stable translation fn, safe to omit

  // Function to ensure data loaded from API has IDs needed for DND
  const addIdsToData = (data: ResumeData): ResumeData => {
    const newData = JSON.parse(JSON.stringify(data)); // Deep clone
    if (newData.experience) {
      newData.experience.forEach((exp: any) => {
        if (!exp.id) exp.id = generateId();
      });
    }
    if (newData.education) {
      newData.education.forEach((edu: any) => {
        if (!edu.id) edu.id = generateId();
      });
    }
    if (newData.skills) {
      // Convert simple string array to object array if needed
      if (newData.skills.length > 0 && typeof newData.skills[0] === "string") {
        newData.skills = newData.skills.map((skill: string) => ({
          id: generateId(),
          value: skill,
        }));
      } else {
        // Ensure existing objects have IDs
        newData.skills.forEach((skill: any) => {
          if (!skill.id) skill.id = generateId();
        });
      }
    } else {
      newData.skills = []; // Ensure skills array exists
    }
    return newData;
  };

  const extractTextFromPDF = async (file: File) => {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await getDocument({ data: arrayBuffer }).promise;
    let text = "";
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const content = await page.getTextContent();
      const pageText = content.items.map((item: any) => item.str).join(" ");
      text += pageText + "\n";
    }
    return text;
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setFileName(file.name);
    setError(""); // Clear error on new upload
    try {
      if (file.type === "application/pdf") {
        const text = await extractTextFromPDF(file);
        setResumeText(text);
      } else if (file.type === "text/plain" || file.name.endsWith(".txt")) {
        // Broader check for text files
        const text = await file.text();
        setResumeText(text);
      } else {
        throw new Error(t("errors.unsupportedFileType"));
      }
    } catch (error: any) {
      console.error("Error processing file:", error);
      setError(`${t("errors.errorProcessingFile")}: ${error.message}`);
      setFileName(""); // Clear filename on error
    }
  };

  const handleTextChange = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    // Changed to handle TextField
    setResumeText(event.target.value);
  };

  const handleJobDescriptionChange = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    // Changed to handle TextField
    setJobDescription(event.target.value);
  };

  const handleOptimize = async () => {
    if (!resumeText.trim() || !jobDescription.trim()) {
      setError(t("errors.provideBoth"));
      return;
    }
    if (optimizeDoneTimerRef.current) {
      window.clearTimeout(optimizeDoneTimerRef.current);
      optimizeDoneTimerRef.current = null;
    }
    setLoading(true);
    setOptimizeFloatingStatus("optimizing");
    setError("");
    setOptimizedJson(null);
    setOriginalOptimizedJson(null);
    setLastRemovedItem(null);
    setGeneratingPdf(false);
    try {
      const response = await fetch(API_ENDPOINTS.optimizeResume, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          resumeText,
          jobDescription,
          style: optimizationStyle,
        }),
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Failed to optimize resume");
      }
      const data = await response.json();
      if (!data.optimizedResumeJson) {
        throw new Error("Backend did not return optimized JSON data.");
      }
      // Ensure the received data has IDs for DND
      const dataWithIds = addIdsToData(data.optimizedResumeJson);
      setOptimizedJson(dataWithIds);
      setOriginalOptimizedJson(JSON.parse(JSON.stringify(dataWithIds))); // Store original with IDs too
      setOptimizeFloatingStatus("done");
      optimizeDoneTimerRef.current = window.setTimeout(() => {
        setOptimizeFloatingStatus("idle");
        optimizeDoneTimerRef.current = null;
      }, 2800);
    } catch (err: any) {
      setOptimizeFloatingStatus("idle");
      setError(err.message || "Failed to optimize resume");
    } finally {
      setLoading(false);
    }
  };

  // Function to navigate the path and get the parent object/array and the final key/index
  const navigatePath = (
    jsonData: any,
    path: string,
  ): { parent: any; finalKey: string | number } | null => {
    const keys = path.match(/([^[.\]]+)|(\[\d+\])/g) || []; // Split by '.' or '[index]'
    let currentLevel = jsonData;
    try {
      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        if (key.startsWith("[")) {
          // Array index access
          const index = parseInt(key.slice(1, -1), 10);
          if (
            !Array.isArray(currentLevel) ||
            index < 0 ||
            index >= currentLevel.length
          ) {
            throw new Error(`Invalid array index ${key} in path`);
          }
          currentLevel = currentLevel[index];
        } else {
          // Object key access
          if (
            typeof currentLevel !== "object" ||
            currentLevel === null ||
            currentLevel[key] === undefined
          ) {
            throw new Error(`Invalid object key "${key}" in path`);
          }
          currentLevel = currentLevel[key];
        }
      }
      const lastKey = keys[keys.length - 1];
      if (lastKey.startsWith("[")) {
        // Final element is array index
        const index = parseInt(lastKey.slice(1, -1), 10);
        if (!Array.isArray(currentLevel) || index < 0) {
          // Allow index equal to length for push simulation if needed elsewhere
          throw new Error(`Invalid final array index ${lastKey}`);
        }
        return { parent: currentLevel, finalKey: index };
      } else {
        // Final element is object key
        if (typeof currentLevel !== "object" || currentLevel === null) {
          throw new Error(`Parent is not an object for final key "${lastKey}"`);
        }
        return { parent: currentLevel, finalKey: lastKey };
      }
    } catch (error: any) {
      console.error(`Error navigating path "${path}":`, error.message);
      return null;
    }
  };

  const handleJsonChange = (path: string, value: any) => {
    if (!optimizedJson) return;
    // Deep clone to avoid modifying state directly
    const updatedJson = JSON.parse(JSON.stringify(optimizedJson));
    const navigationResult = navigatePath(updatedJson, path);

    if (navigationResult) {
      const { parent, finalKey } = navigationResult;
      parent[finalKey] = value;
      if (path === "contact.link") {
        if (value && parent.behance) {
          delete parent.behance;
        }
        if (!value && parent.behance) {
          delete parent.behance;
        }
      }
      setOptimizedJson(updatedJson);
    } else {
      console.error("Failed to apply change, invalid path:", path);
      setError(`Failed to update data at path: ${path}`); // Provide user feedback
    }
  };

  const duplicateArrayItem = (arrayPath: string, index: number) => {
    if (!optimizedJson) return;
    const updatedJson = JSON.parse(JSON.stringify(optimizedJson));
    const navigationResult = navigatePath(updatedJson, arrayPath);

    if (navigationResult) {
      const { parent, finalKey } = navigationResult;

      let targetArray: any[] | undefined;

      if (Array.isArray(parent)) {
        targetArray = parent;
      } else if (
        typeof finalKey === "string" &&
        Array.isArray(parent[finalKey])
      ) {
        targetArray = parent[finalKey];
      }

      if (targetArray) {
        const sourceItem = targetArray[index];
        if (sourceItem) {
          const newItem =
            typeof sourceItem === "object" && sourceItem !== null
              ? {
                  ...JSON.parse(JSON.stringify(sourceItem)),
                  id: generateId(),
                }
              : sourceItem;

          targetArray.splice(index + 1, 0, newItem);
          setOptimizedJson(updatedJson);
          return;
        }
      }
    }

    console.error(
      "Failed to duplicate item, invalid path or not an array:",
      arrayPath,
    );
    setError(`Failed to duplicate item at path: ${arrayPath}`);
  };

  const addArrayItem = (arrayPath: string, newItem: any) => {
    if (!optimizedJson) return;
    const updatedJson = JSON.parse(JSON.stringify(optimizedJson));
    const navigationResult = navigatePath(updatedJson, arrayPath);

    if (navigationResult) {
      const { parent, finalKey } = navigationResult;
      // Ensure parent is an array before pushing
      if (Array.isArray(parent)) {
        parent.push(newItem);
        setOptimizedJson(updatedJson);
        setLastRemovedItem(null); // Clear undo state on add
      } else if (
        typeof finalKey === "string" &&
        Array.isArray(parent[finalKey])
      ) {
        // Handle case where path points to the array key itself (e.g., 'experience')
        parent[finalKey].push(newItem);
        setOptimizedJson(updatedJson);
        setLastRemovedItem(null); // Clear undo state on add
      } else {
        console.error(
          `Target path "${arrayPath}" does not resolve to an array.`,
        );
        setError(
          `Cannot add item: Target path "${arrayPath}" is not an array.`,
        );
      }
    } else {
      console.error("Failed to add item, invalid path:", arrayPath);
      setError(`Failed to add item: Invalid path "${arrayPath}".`);
    }
  };

  const removeArrayItem = (arrayPathWithIndex: string) => {
    if (!optimizedJson) return;
    const updatedJson = JSON.parse(JSON.stringify(optimizedJson));
    const navigationResult = navigatePath(updatedJson, arrayPathWithIndex);

    if (navigationResult) {
      const { parent, finalKey } = navigationResult;
      if (
        Array.isArray(parent) &&
        typeof finalKey === "number" &&
        finalKey >= 0 &&
        finalKey < parent.length
      ) {
        const removed = parent.splice(finalKey, 1)[0]; // Remove item and get it
        // Store info needed to potentially undo this removal
        // Need the path to the *parent array* itself
        const arrayPathParts =
          arrayPathWithIndex.match(/([^[.\]]+)|(\[\d+\])/g) || [];
        const parentPath = arrayPathParts.slice(0, -1).join(""); // Reconstruct path to parent
        setLastRemovedItem({
          item: removed,
          path: parentPath,
          index: finalKey,
        });
        setOptimizedJson(updatedJson);
      } else {
        console.error(
          `Invalid array index or parent is not an array for path ${arrayPathWithIndex}`,
        );
        setError(
          `Cannot remove item: Invalid path or index "${arrayPathWithIndex}".`,
        );
      }
    } else {
      console.error("Failed to remove item, invalid path:", arrayPathWithIndex);
      setError(`Failed to remove item: Invalid path "${arrayPathWithIndex}".`);
    }
  };

  const handleRevertLastRemoval = () => {
    if (!lastRemovedItem || !optimizedJson) return;

    const { item, path, index } = lastRemovedItem;
    const updatedJson = JSON.parse(JSON.stringify(optimizedJson));
    const navigationResult = navigatePath(updatedJson, path); // Navigate to the parent array

    if (navigationResult) {
      const { parent, finalKey } = navigationResult;
      let targetArray: any[] | null = null;

      // Find the target array based on navigation result
      if (Array.isArray(parent)) {
        // If path directly pointed to the array (e.g., after navigating complex path)
        targetArray = parent;
      } else if (
        typeof finalKey === "string" &&
        Array.isArray(parent[finalKey])
      ) {
        // If path pointed to the object containing the array
        targetArray = parent[finalKey];
      }

      if (targetArray !== null) {
        targetArray.splice(index, 0, item); // Insert item back at original index
        setOptimizedJson(updatedJson);
        setLastRemovedItem(null); // Clear undo state after reverting
      } else {
        console.error(
          `Could not find target array at path ${path} to revert removal.`,
        );
        setError(`Undo failed: Could not find target array.`);
      }
    } else {
      console.error("Failed to revert removal, invalid path:", path);
      setError(`Undo failed: Invalid path "${path}".`);
    }
  };

  const handleResetJson = () => {
    if (originalOptimizedJson) {
      setOptimizedJson(JSON.parse(JSON.stringify(originalOptimizedJson))); // Restore from original copy
      setLastRemovedItem(null); // Clear undo state
      setError(""); // Clear any errors
    }
  };

  const handleGeneratePdf = async () => {
    if (!optimizedJson) {
      setError(t("errors.noOptimizedData"));
      return;
    }
    setGeneratingPdf(true);
    setError("");
    try {
      // Prepare data for backend: remove temporary IDs used for DND
      const dataToSend = JSON.parse(JSON.stringify(optimizedJson));
      if (dataToSend.experience)
        dataToSend.experience.forEach((item: any) => delete item.id);
      if (dataToSend.education)
        dataToSend.education.forEach((item: any) => delete item.id);
      if (
        dataToSend.skills &&
        dataToSend.skills.length > 0 &&
        typeof dataToSend.skills[0] === "object"
      ) {
        // Convert skills back to string array for templates
        dataToSend.skills = dataToSend.skills.map((skill: any) => skill.value);
      }

      const response = await fetch(API_ENDPOINTS.generatePdf, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          resumeData: dataToSend,
          templateName: selectedTemplate,
          accentColor: templateSupportsAccent ? accentColor : undefined,
        }),
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Failed to generate PDF");
      }
      const data = await response.json();
      if (data.pdfBase64) {
        handleDownloadPdf(data.pdfBase64);
      } else {
        throw new Error("PDF data not received from server.");
      }
    } catch (err: any) {
      setError(err.message || "Failed to generate PDF");
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleDownloadPdf = (base64String: string) => {
    try {
      const byteCharacters = atob(base64String);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      // Extract name and optimized profession title
      const namePart = (optimizedJson?.name || "resume")
        .replace(/[^a-z0-9\s-]/gi, "") // Allow spaces and hyphens initially
        .trim()
        .replace(/\s+/g, "-") // Replace spaces with hyphens
        .toLowerCase();
      const optimizedProfessionPart = (
        optimizedJson?.experience?.[0]?.title ||
        searchLinkTitle ||
        "profession"
      )
        .replace(/[^a-z0-9\s-]/gi, "")
        .trim()
        .replace(/\s+/g, "-")
        .toLowerCase();
      const datePart = new Date().toISOString().split("T")[0]; // YYYY-MM-DD format

      // Construct filename: myname-optimizedprofession-today'sdate.pdf
      link.download = `${namePart}-${optimizedProfessionPart}-${datePart}.pdf`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Track Meta Pixel conversion (Lead event for PDF download)
      if (typeof (window as any).fbq !== "undefined") {
        (window as any).fbq("track", "Lead");
      }

      URL.revokeObjectURL(url); // Clean up
      setError(""); // Clear error on successful download
    } catch (error: any) {
      console.error("Error creating download link:", error);
      setError(`Failed to initiate download: ${error.message}`);
    }
  };

  const clearJobDescription = () => {
    setJobDescription("");
    setError("");
  };

  const clearResumeText = () => {
    setResumeText("");
    setFileName(""); // Clear file name as well
    if (fileInputRef.current) {
      fileInputRef.current.value = ""; // Reset file input visually
    }
    setError("");
  };

  // Drag and Drop Handler
  const onDragEnd = (result: DropResult) => {
    console.log("--- onDragEnd triggered ---", result);
    const { source, destination, type } = result;

    // Log values before the check
    console.log("[onDragEnd] Checking destination:", destination);
    console.log(
      "[onDragEnd] Checking optimizedJson state:",
      optimizedJson ? "Exists" : "NULL or undefined",
    );

    // Dropped outside the list or no JSON
    if (!destination || !optimizedJson) {
      return;
    }

    // Determine which list type was dragged
    let listPath: keyof ResumeData | null = null;
    if (type === "experience") listPath = "experience";
    else if (type === "education") listPath = "education";
    else if (type === "skills") listPath = "skills";
    else if (type === "details") {
      // Need to find the specific experience item's details array
      const droppableIdParts = source.droppableId.split("-"); // e.g., "details-id-timestamp-random"
      // Check if first part is 'details' and there are subsequent parts for the ID
      if (droppableIdParts.length > 1 && droppableIdParts[0] === "details") {
        // Reconstruct the expId by joining all parts after the first one
        const expId = droppableIdParts.slice(1).join("-"); // -> "id-timestamp-random"
        console.log(`[onDragEnd - Details] Extracted expId: ${expId}`); // Log extracted ID
        // Find the index of the experience item based on the ID from the droppableId
        const expIndex = optimizedJson.experience.findIndex(
          (exp) => exp.id === expId,
        );
        console.log(
          `[onDragEnd - Details] Result of findIndex for expId ${expId}: ${expIndex}`,
        ); // Log findIndex result

        // If findIndex returns -1 (not found), this block is skipped!
        if (expIndex !== -1) {
          // --- Reorder details ---
          console.log(
            `[onDragEnd - Details] Reordering details for expId: ${expId}, Source: ${source.index}, Dest: ${destination.index}`,
          ); // Log details drag
          const updatedJson = JSON.parse(JSON.stringify(optimizedJson)); // Clone
          const items = Array.from(updatedJson.experience[expIndex].details); // Get details array
          console.log(
            "[onDragEnd - Details] List before reorder:",
            JSON.stringify(items),
          ); // Log before
          const [reorderedItem] = items.splice(source.index, 1); // Remove
          items.splice(destination.index, 0, reorderedItem); // Insert
          console.log(
            "[onDragEnd - Details] List after reorder:",
            JSON.stringify(items),
          ); // Log after
          updatedJson.experience[expIndex].details = items; // Update details array
          console.log("[onDragEnd - Details] Setting updated JSON state...");
          setOptimizedJson(updatedJson); // Update state
        }
      }
      return; // Handled details drag separately
    }

    if (!listPath) return; // Unknown draggable type

    // --- Reorder top-level lists (experience, education, skills) ---
    console.log(
      `[onDragEnd] Reordering list type: ${listPath}, Source: ${source.index}, Destination: ${destination.index}`,
    ); // Log basic info
    const updatedJson = JSON.parse(JSON.stringify(optimizedJson)); // Clone
    const list = Array.from(updatedJson[listPath] as any[]); // Get the correct list

    if (!list) {
      console.error(`[onDragEnd] Could not find list for path: ${listPath}`); // Added error log
      return;
    }

    console.log(
      `[onDragEnd] List (${listPath}) before reorder:`,
      JSON.stringify(list.map((item: any) => item.id || item.value)),
    ); // Log IDs/values before

    const [reorderedItem] = list.splice(source.index, 1); // Remove
    console.log(
      `[onDragEnd] Item removed from index ${source.index}:`,
      JSON.stringify(reorderedItem),
    );

    list.splice(destination.index, 0, reorderedItem); // Insert
    console.log(
      `[onDragEnd] List (${listPath}) after reorder:`,
      JSON.stringify(list.map((item: any) => item.id || item.value)),
    ); // Log IDs/values after

    updatedJson[listPath] = list; // Update the list in the cloned object
    console.log("[onDragEnd] Setting updated JSON state...");
    setOptimizedJson(updatedJson); // Update state
  };

  // --- JSX Structure ---
  if (currentPage === "privacy") return <PrivacyPolicy />;
  if (currentPage === "about") return <About />;
  if (currentPage === "blog") return <Blog />;
  if (currentPage === "blog-post") {
    const slug = window.location.hash.replace("#/blog/", "");
    return <BlogPost slug={slug} />;
  }

  return (
    <>
      <SEOHead
        language={isRTL ? "ar" : "en"}
        title={
          isRTL
            ? "أداة تحسين السيرة الذاتية بالذكاء الاصطناعي | محسن السيرة الذاتية المجاني"
            : undefined
        }
        description={
          isRTL
            ? "عزز نجاح طلب التوظيف الخاص بك مع محسن السيرة الذاتية المجاني بالذكاء الاصطناعي. احصل على سيرة ذاتية متوافقة مع أنظمة تتبع المتقدمين في ثوانٍ."
            : undefined
        }
      />
      {/* Top-right controls: home brand + language + theme — grouped for alignment */}
      <ControlsBar />
      {optimizeFloatingStatus !== "idle" && (
        <Box
          className={`floating-optimize-status is-${optimizeFloatingStatus}`}
          role="status"
          aria-live="polite"
        >
          {optimizeFloatingStatus === "optimizing" ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            <CheckCircleOutlineIcon fontSize="small" />
          )}
          <Typography component="span" variant="button">
            {optimizeFloatingStatus === "optimizing"
              ? t("buttons.optimizing")
              : t("buttons.done")}
          </Typography>
        </Box>
      )}
      <DragDropContext onDragEnd={onDragEnd}>
        <Box className="ambient-stage" aria-hidden="true">
          <Box className="orb orb-one" />
          <Box className="orb orb-two" />
          <Box className="orb orb-three" />
        </Box>
        <Container className="app-shell" maxWidth="xl">
          <Paper className="hero-panel" elevation={0}>
            <Grid container spacing={4} alignItems="center">
              <Grid size={{ xs: 12, md: 7 }}>
                <Typography variant="h2" component="h1" className="hero-title">
                  {t("app.title")}
                </Typography>
                <Typography variant="h6" className="hero-subtitle">
                  {t("app.subtitle")}
                </Typography>
                <Stack
                  className="hero-feature-chips"
                  direction="row"
                  flexWrap="wrap"
                  useFlexGap
                  spacing={1.5}
                  sx={{ mt: 3 }}
                >
                  <Chip
                    className="hero-feature-chip"
                    icon={<CheckCircleOutlineIcon />}
                    label={t("heroBadges.atsFriendlyRewrite")}
                  />
                  <Chip
                    className="hero-feature-chip"
                    icon={<TipsAndUpdatesIcon />}
                    label={t("heroBadges.editableSections")}
                  />
                  <Chip
                    className="hero-feature-chip"
                    icon={<RocketLaunchIcon />}
                    label={t("heroBadges.pdfExport")}
                  />
                </Stack>
              </Grid>
              <Grid size={{ xs: 12, md: 5 }}>
                <Box className="hero-simple-card">
                  <Typography variant="overline">
                    {t("heroHowItWorks.title")}
                  </Typography>
                  <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                    {[
                      t("heroHowItWorks.step1"),
                      t("heroHowItWorks.step2"),
                      t("heroHowItWorks.step3"),
                    ].map((step, index) => (
                      <Stack
                        key={step}
                        className="hero-step-row"
                        direction="row"
                        alignItems="flex-start"
                      >
                        <Box className="step-number">{index + 1}</Box>
                        <Typography className="hero-step-text" variant="body2">
                          {step}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              </Grid>
            </Grid>
          </Paper>
          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 3,
                borderRadius: 3,
                backgroundColor: "rgba(200,50,50,0.08)",
                backdropFilter: "blur(12px)",
                border: "1px solid rgba(200,50,50,0.2)",
                color: "text.primary",
                "& .MuiAlert-icon": { color: "#e05555" },
                "& .MuiAlert-message": { fontWeight: 500 },
              }}
            >
              {error}
            </Alert>
          )}
          <Grid container spacing={4}>
            {/* Input Section (Left Column) */}
            <Grid size={{ xs: 12, md: 5 }}>
              <Paper
                className="glass-panel input-panel"
                elevation={0}
                sx={{
                  p: 4,
                  height: "100%",
                  borderRadius: 3,
                }}
              >
                <Typography
                  variant="h5"
                  component="h2"
                  gutterBottom
                  sx={{ mb: 4, fontWeight: 600 }}
                >
                  {t("sections.input")}
                </Typography>
                <Stack spacing={3.5}>
                  {/* File Upload */}
                  <Box>
                    <Button
                      className="resume-upload-button"
                      component="label"
                      variant="outlined"
                      startIcon={<CloudUploadIcon />}
                      fullWidth
                    >
                      {t("buttons.uploadResume")}
                      <input
                        type="file"
                        hidden
                        accept=".pdf,.txt,text/plain,application/pdf"
                        onChange={handleFileUpload}
                        ref={fileInputRef}
                      />
                    </Button>
                    {fileName && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {fileName}
                      </Typography>
                    )}
                  </Box>

                  {/* Resume Text */}
                  <Box sx={{ position: "relative" }}>
                    <TextField
                      label={t("labels.resumeText")}
                      multiline
                      rows={10}
                      value={resumeText}
                      onChange={handleTextChange}
                      variant="outlined"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                    {resumeText && (
                      <IconButton
                        onClick={clearResumeText}
                        size="small"
                        sx={{
                          position: "absolute",
                          top: -14,
                          right: 0,
                          color: "text.secondary",
                          backgroundColor: "background.paper",
                          border: "1px solid",
                          borderColor: "divider",
                          width: 22,
                          height: 22,
                          "&:hover": {
                            backgroundColor: "action.hover",
                            color: "text.primary",
                          },
                        }}
                        title={t("placeholders.clearResumeText")}
                      >
                        <ClearIcon sx={{ fontSize: "0.75rem" }} />
                      </IconButton>
                    )}
                  </Box>

                  {/* Job Description */}
                  <Box sx={{ position: "relative" }}>
                    <TextField
                      label={t("labels.jobDescription")}
                      multiline
                      rows={6}
                      value={jobDescription}
                      onChange={handleJobDescriptionChange}
                      variant="outlined"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                    {jobDescription && (
                      <IconButton
                        onClick={clearJobDescription}
                        size="small"
                        sx={{
                          position: "absolute",
                          top: -14,
                          right: 0,
                          color: "text.secondary",
                          backgroundColor: "background.paper",
                          border: "1px solid",
                          borderColor: "divider",
                          width: 22,
                          height: 22,
                          "&:hover": {
                            backgroundColor: "action.hover",
                            color: "text.primary",
                          },
                        }}
                        title={t("placeholders.clearJobDescription")}
                      >
                        <ClearIcon sx={{ fontSize: "0.75rem" }} />
                      </IconButton>
                    )}
                  </Box>

                  {/* --- Editable Job Title for Search Links --- */}
                  <TextField
                    label={t("labels.jobTitleForSearch")}
                    value={searchLinkTitle}
                    onChange={(e) => setSearchLinkTitle(e.target.value)}
                    variant="outlined"
                    fullWidth
                    size="small"
                    sx={{ mt: 2 }}
                    helperText={
                      titleExtractionLoading
                        ? t("placeholders.gettingTitleSuggestion")
                        : titleExtractionError
                          ? titleExtractionError
                          : t("placeholders.editIfIncorrect")
                    }
                    error={!!titleExtractionError} // Highlight field on error
                  />

                  {/* Optimization Style */}
                  <FormControl fullWidth>
                    <InputLabel id="optimization-style-label">
                      {t("labels.optimizationStyle")}
                    </InputLabel>
                    <Select
                      labelId="optimization-style-label"
                      value={optimizationStyle}
                      label={t("labels.optimizationStyle")}
                      onChange={(e: SelectChangeEvent) =>
                        setOptimizationStyle(e.target.value)
                      }
                    >
                      <MenuItem value="Default">
                        {t("options.default")}
                      </MenuItem>
                      <MenuItem value="Concise">
                        {t("options.concise")}
                      </MenuItem>
                      <MenuItem value="Technical">
                        {t("options.technical")}
                      </MenuItem>
                      <MenuItem value="Leadership">
                        {t("options.leadership")}
                      </MenuItem>
                    </Select>
                  </FormControl>

                  {/* Optimize Button */}
                  <Button
                    className={`optimize-button ${loading ? "is-optimizing" : ""}`}
                    variant="contained"
                    color="primary"
                    onClick={handleOptimize}
                    disabled={loading || generatingPdf}
                    startIcon={
                      loading ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <BuildIcon />
                      )
                    }
                    size="large"
                    fullWidth
                  >
                    {loading
                      ? t("buttons.optimizing")
                      : t("buttons.optimizeResume")}
                  </Button>
                  <Box
                    className={`mobile-optimize-cue ${loading ? "is-loading" : ""} ${optimizedJson ? "is-ready" : ""}`}
                    aria-live="polite"
                  >
                    <AutoAwesomeIcon fontSize="small" />
                    <Typography variant="body2">
                      {loading
                        ? t("mobileStatus.optimizing")
                        : optimizedJson
                          ? t("mobileStatus.ready")
                          : t("mobileStatus.prompt")}
                    </Typography>
                  </Box>
                </Stack>

                {/* --- Job Search Links — location-aware --- */}
                {searchLinkTitle.trim() && (
                  <Paper
                    className="job-links-panel"
                    variant="outlined"
                    sx={{
                      p: 3,
                      mt: 3,
                      borderRadius: 2,
                      background: "rgba(148, 163, 184, 0.05)",
                    }}
                  >
                    <Stack direction="row" alignItems="baseline" spacing={0.6} sx={{ mb: 2 }} flexWrap="wrap">
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {geo.loading
                          ? t("jobSearch.findJobsNearYou")
                          : `${t("jobSearch.findJobsIn")} ${geo.countryName}`}
                      </Typography>
                      {!geo.loading && (
                        <Typography variant="caption" color="text.disabled">
                          · {t("jobSearch.platformsFor")} {geo.countryName}
                        </Typography>
                      )}
                    </Stack>
                    <Grid container spacing={1.25} className="job-links-grid">
                      {geo.loading
                        ? // Skeleton placeholders while geo loads
                          [1, 2, 3].map((i) => (
                            <Grid size={{ xs: 12, sm: "auto" }} key={i}>
                              <Box
                                sx={{
                                  height: 30,
                                  width: 110,
                                  borderRadius: 1,
                                  backgroundColor: "action.hover",
                                  animation: "pulse 1.4s ease-in-out infinite",
                                }}
                              />
                            </Grid>
                          ))
                        : getJobLinks(searchLinkTitle).map((link) => (
                            <Grid size={{ xs: 12, sm: "auto" }} key={link.name}>
                              <Button
                                variant="outlined"
                                size="small"
                                href={link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                  justifyContent: "center",
                                  width: { xs: "100%", sm: "auto" },
                                }}
                              >
                                {link.name}
                              </Button>
                            </Grid>
                          ))}
                    </Grid>
                  </Paper>
                )}
              </Paper>
            </Grid>
            {/* Output Section (Right Column) - Conditional */}
            {optimizedJson && (
              <Grid size={{ xs: 12, md: 7 }}>
                <Paper
                  className="glass-panel output-panel"
                  elevation={0}
                  sx={{
                    p: 4,
                    height: "100%",
                    borderRadius: 3,
                  }}
                >
                  <Stack
                    className="output-header-row"
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "stretch", sm: "center" }}
                    spacing={2}
                    sx={{ mb: 3 }}
                  >
                    <Typography
                      variant="h5"
                      component="h2"
                      sx={{ fontWeight: 600 }}
                    >
                      {t("sections.output")}
                    </Typography>
                    <Stack
                      className="output-edit-actions"
                      direction="row"
                      spacing={1}
                    >
                      <Button
                        className="secondary-action-button"
                        variant="outlined"
                        onClick={handleRevertLastRemoval}
                        disabled={!lastRemovedItem || loading || generatingPdf}
                        startIcon={<UndoIcon />}
                        size="small"
                      >
                        <Box component="span" className="button-label-full">
                          {t("buttons.undoRemove")}
                        </Box>
                        <Box component="span" className="button-label-short">
                          {t("buttons.undoShort")}
                        </Box>
                      </Button>
                      <Button
                        className="secondary-action-button"
                        variant="outlined"
                        color="secondary"
                        onClick={handleResetJson}
                        disabled={
                          !originalOptimizedJson || loading || generatingPdf
                        }
                        startIcon={<RestartAltIcon />}
                        size="small"
                      >
                        <Box component="span" className="button-label-full">
                          {t("buttons.resetEdits")}
                        </Box>
                        <Box component="span" className="button-label-short">
                          {t("buttons.resetShort")}
                        </Box>
                      </Button>
                    </Stack>
                  </Stack>
                  <Stack spacing={3}>
                    {/* Template Selection and Download */}
                    <Stack
                      className="pdf-actions-row"
                      direction={{ xs: "column", sm: "row" }}
                      spacing={2}
                      alignItems={{ xs: "stretch", sm: "center" }}
                    >
                      <FormControl
                        className="pdf-template-select"
                        variant="outlined"
                        size="small"
                        sx={{ minWidth: 200, flexGrow: 1 }}
                      >
                        <InputLabel id="template-select-label">
                          {t("labels.pdfTemplate")}
                        </InputLabel>
                        <Select
                          labelId="template-select-label"
                          value={selectedTemplate}
                          label={t("labels.pdfTemplate")}
                          onChange={(e) => setSelectedTemplate(e.target.value)}
                        >
                          {availableTemplates.map((template) => (
                            <MenuItem
                              key={template.value}
                              value={template.value}
                            >
                              {t(`templates.${template.value}`)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Button
                        className={`pdf-generate-button ${generatingPdf ? "is-generating" : ""}`}
                        variant="contained"
                        color="primary"
                        onClick={handleGeneratePdf}
                        disabled={generatingPdf || loading}
                        startIcon={
                          generatingPdf ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : (
                            <DownloadIcon />
                          )
                        }
                      >
                        <Box
                          component="span"
                          className="pdf-generate-label-full"
                        >
                          {generatingPdf
                            ? t("buttons.generating")
                            : t("buttons.generateDownload")}
                        </Box>
                        <Box
                          component="span"
                          className="pdf-generate-label-short"
                        >
                          {generatingPdf
                            ? t("buttons.generatingShort")
                            : t("buttons.generatePdfShort")}
                        </Box>
                      </Button>
                    </Stack>

                    {/* Accent color palette — only for Slate and Executive */}
                    {templateSupportsAccent && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                        <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, mr: 0.5 }}>
                          Accent color
                        </Typography>
                        {ACCENT_PALETTE.map((color) => (
                          <Box
                            key={color}
                            onClick={() => setAccentColor(color)}
                            sx={{
                              width: 22, height: 22, borderRadius: "50%",
                              bgcolor: color, cursor: "pointer", flexShrink: 0,
                              outline: accentColor === color ? `3px solid ${color}` : "none",
                              outlineOffset: "2px",
                              border: "2px solid",
                              borderColor: accentColor === color ? "background.paper" : "transparent",
                              transition: "transform 0.15s ease",
                              "&:hover": { transform: "scale(1.2)" },
                            }}
                          />
                        ))}
                        <Box
                          onClick={shuffleAccent}
                          sx={{
                            width: 22, height: 22, borderRadius: "50%", flexShrink: 0,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            cursor: "pointer", border: "1.5px dashed",
                            borderColor: "text.disabled", color: "text.disabled",
                            fontSize: "0.8rem", lineHeight: 1,
                            transition: "transform 0.15s ease, color 0.15s ease",
                            "&:hover": { transform: "scale(1.2)", color: "text.secondary" },
                          }}
                        >
                          ↻
                        </Box>
                      </Box>
                    )}

                    {/* --- Editable Fields --- */}

                    {/* Name */}
                    <TextField
                      label={t("labels.fullName")}
                      value={optimizedJson.name}
                      onChange={(e) => handleJsonChange("name", e.target.value)}
                      variant="outlined"
                      fullWidth
                      size="small"
                      disabled={loading || generatingPdf}
                    />

                    {/* Profession Title (AI-detected) */}
                    <TextField
                      label={t("labels.professionTitle")}
                      value={searchLinkTitle}
                      onChange={(e) => setSearchLinkTitle(e.target.value)}
                      variant="outlined"
                      fullWidth
                      size="small"
                      disabled={loading || generatingPdf}
                      helperText={t("placeholders.derivedFromJobTitle")}
                    />

                    {/* Contact Details */}
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="subtitle1" gutterBottom>
                        {t("labels.contact")}
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            label={t("labels.email")}
                            value={optimizedJson.contact.email}
                            onChange={(e) =>
                              handleJsonChange("contact.email", e.target.value)
                            }
                            variant="outlined"
                            fullWidth
                            size="small"
                            disabled={loading || generatingPdf}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            label={t("labels.phone")}
                            value={optimizedJson.contact.phone}
                            onChange={(e) =>
                              handleJsonChange("contact.phone", e.target.value)
                            }
                            variant="outlined"
                            fullWidth
                            size="small"
                            disabled={loading || generatingPdf}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            label={t("labels.location")}
                            value={optimizedJson.contact.location}
                            onChange={(e) =>
                              handleJsonChange(
                                "contact.location",
                                e.target.value,
                              )
                            }
                            variant="outlined"
                            fullWidth
                            size="small"
                            disabled={loading || generatingPdf}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            label={t("labels.websiteOrPortfolio")}
                            value={
                              optimizedJson.contact.link ||
                              optimizedJson.contact.behance ||
                              ""
                            }
                            onChange={(e) =>
                              handleJsonChange("contact.link", e.target.value)
                            }
                            variant="outlined"
                            fullWidth
                            size="small"
                            disabled={loading || generatingPdf}
                          />
                        </Grid>
                      </Grid>
                    </Paper>

                    {/* Summary */}
                    <TextField
                      label={t("labels.summary")}
                      multiline
                      rows={4}
                      value={optimizedJson.summary}
                      onChange={(e) =>
                        handleJsonChange("summary", e.target.value)
                      }
                      variant="outlined"
                      fullWidth
                      size="small"
                      disabled={loading || generatingPdf}
                    />

                    {/* Experience Section */}
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ mb: 1 }}
                      >
                        <Typography variant="subtitle1">
                          {t("labels.experience")}
                        </Typography>
                        <Button
                          startIcon={<AddCircleOutlineIcon />}
                          onClick={() =>
                            addArrayItem("experience", {
                              id: generateId(),
                              title: "",
                              company: "",
                              dates: "",
                              details: [""],
                            })
                          }
                          size="small"
                          disabled={loading || generatingPdf}
                        >
                          {t("buttons.addExperience")}
                        </Button>
                      </Stack>
                      <Droppable droppableId="experience" type="experience">
                        {(provided) => (
                          <Box
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                          >
                            <Stack spacing={2}>
                              {optimizedJson.experience.map((exp, index) => (
                                <Draggable
                                  key={exp.id}
                                  draggableId={exp.id!}
                                  index={index}
                                >
                                  {(providedDrag) => (
                                    <Paper
                                      variant="outlined"
                                      sx={{ p: 2, position: "relative" }}
                                      ref={providedDrag.innerRef}
                                      {...providedDrag.draggableProps}
                                    >
                                      <Box
                                        {...providedDrag.dragHandleProps}
                                        sx={{
                                          position: "absolute",
                                          top: 8,
                                          left: 8,
                                          opacity: 0.5,
                                          cursor: "grab",
                                        }}
                                      >
                                        <DragIndicatorIcon />
                                      </Box>
                                      <Box
                                        sx={{
                                          position: "absolute",
                                          top: 8,
                                          right: 8,
                                          display: "flex",
                                          gap: 1,
                                        }}
                                      >
                                        <IconButton
                                          onClick={() =>
                                            duplicateArrayItem(
                                              "experience",
                                              index,
                                            )
                                          }
                                          size="small"
                                          color="primary"
                                          title={t("buttons.duplicateExperience")}
                                          disabled={loading || generatingPdf}
                                        >
                                          <ContentCopyIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                          onClick={() =>
                                            removeArrayItem(
                                              `experience[${index}]`,
                                            )
                                          }
                                          size="small"
                                          color="error"
                                          title={t(
                                            "placeholders.removeExperience",
                                          )}
                                          disabled={loading || generatingPdf}
                                        >
                                          <RemoveCircleOutlineIcon fontSize="small" />
                                        </IconButton>
                                      </Box>

                                      <Stack spacing={1.5} sx={{ pl: 3 }}>
                                        {" "}
                                        {/* Indent content */}
                                        <TextField
                                          label={t("labels.jobTitle")}
                                          value={exp.title}
                                          onChange={(e) =>
                                            handleJsonChange(
                                              `experience[${index}].title`,
                                              e.target.value,
                                            )
                                          }
                                          variant="standard"
                                          fullWidth
                                          size="small"
                                          disabled={loading || generatingPdf}
                                        />
                                        <TextField
                                          label={t("labels.company")}
                                          value={exp.company}
                                          onChange={(e) =>
                                            handleJsonChange(
                                              `experience[${index}].company`,
                                              e.target.value,
                                            )
                                          }
                                          variant="standard"
                                          fullWidth
                                          size="small"
                                          disabled={loading || generatingPdf}
                                        />
                                        <TextField
                                          label={t("labels.dates")}
                                          value={exp.dates}
                                          onChange={(e) =>
                                            handleJsonChange(
                                              `experience[${index}].dates`,
                                              e.target.value,
                                            )
                                          }
                                          variant="standard"
                                          fullWidth
                                          size="small"
                                          disabled={loading || generatingPdf}
                                        />
                                        <Typography
                                          variant="body2"
                                          sx={{ mt: 1, fontWeight: "bold" }}
                                        >
                                          {t("labels.details")}:
                                        </Typography>
                                        <Droppable
                                          droppableId={`details-${exp.id}`}
                                          type="details"
                                        >
                                          {(providedDetails) => (
                                            <Stack
                                              spacing={1}
                                              ref={providedDetails.innerRef}
                                              {...providedDetails.droppableProps}
                                            >
                                              {exp.details.map(
                                                (detail, detailIndex) => (
                                                  <Draggable
                                                    key={`${exp.id}-detail-${detailIndex}`}
                                                    draggableId={`${exp.id}-detail-${detailIndex}`}
                                                    index={detailIndex}
                                                  >
                                                    {(providedDetailDrag) => (
                                                      <Box
                                                        display="flex"
                                                        alignItems="center"
                                                        ref={
                                                          providedDetailDrag.innerRef
                                                        }
                                                        {...providedDetailDrag.draggableProps}
                                                      >
                                                        <Box
                                                          {...providedDetailDrag.dragHandleProps}
                                                          sx={{
                                                            opacity: 0.5,
                                                            cursor: "grab",
                                                            mr: 1,
                                                            display: "flex",
                                                            alignItems:
                                                              "center",
                                                          }}
                                                        >
                                                          <DragIndicatorIcon fontSize="small" />
                                                        </Box>
                                                        <TextField
                                                          multiline
                                                          value={detail}
                                                          onChange={(e) =>
                                                            handleJsonChange(
                                                              `experience[${index}].details[${detailIndex}]`,
                                                              e.target.value,
                                                            )
                                                          }
                                                          variant="standard"
                                                          fullWidth
                                                          size="small"
                                                          disabled={
                                                            loading ||
                                                            generatingPdf
                                                          }
                                                        />
                                                        <IconButton
                                                          onClick={() =>
                                                            removeArrayItem(
                                                              `experience[${index}].details[${detailIndex}]`,
                                                            )
                                                          }
                                                          size="small"
                                                          title={t(
                                                            "placeholders.removeDetail",
                                                          )}
                                                          disabled={
                                                            loading ||
                                                            generatingPdf
                                                          }
                                                        >
                                                          <RemoveCircleOutlineIcon fontSize="small" />
                                                        </IconButton>
                                                      </Box>
                                                    )}
                                                  </Draggable>
                                                ),
                                              )}
                                              {providedDetails.placeholder}
                                            </Stack>
                                          )}
                                        </Droppable>
                                        <Button
                                          startIcon={<AddCircleOutlineIcon />}
                                          onClick={() =>
                                            addArrayItem(
                                              `experience[${index}].details`,
                                              "",
                                            )
                                          }
                                          size="small"
                                          sx={{ alignSelf: "flex-start" }}
                                          disabled={loading || generatingPdf}
                                        >
                                          {t("buttons.addDetail")}
                                        </Button>
                                      </Stack>
                                    </Paper>
                                  )}
                                </Draggable>
                              ))}
                              {provided.placeholder}
                            </Stack>
                          </Box>
                        )}
                      </Droppable>
                    </Paper>

                    {/* Education Section */}
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ mb: 1 }}
                      >
                        <Typography variant="subtitle1">
                          {t("labels.education")}
                        </Typography>
                        <Button
                          startIcon={<AddCircleOutlineIcon />}
                          onClick={() =>
                            addArrayItem("education", {
                              id: generateId(),
                              degree: "",
                              institution: "",
                              dates: "",
                            })
                          }
                          size="small"
                          disabled={loading || generatingPdf}
                        >
                          {t("buttons.addEducation")}
                        </Button>
                      </Stack>
                      <Droppable droppableId="education" type="education">
                        {(provided) => (
                          <Box
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                          >
                            <Stack spacing={2}>
                              {optimizedJson.education.map((edu, index) => (
                                <Draggable
                                  key={edu.id}
                                  draggableId={edu.id!}
                                  index={index}
                                >
                                  {(providedDrag) => (
                                    <Paper
                                      variant="outlined"
                                      sx={{ p: 2, position: "relative" }}
                                      ref={providedDrag.innerRef}
                                      {...providedDrag.draggableProps}
                                    >
                                      <Box
                                        {...providedDrag.dragHandleProps}
                                        sx={{
                                          position: "absolute",
                                          top: 8,
                                          left: 8,
                                          opacity: 0.5,
                                          cursor: "grab",
                                        }}
                                      >
                                        <DragIndicatorIcon />
                                      </Box>
                                      <Box
                                        sx={{
                                          position: "absolute",
                                          top: 8,
                                          right: 8,
                                          display: "flex",
                                          gap: 1,
                                        }}
                                      >
                                        <IconButton
                                          onClick={() =>
                                            duplicateArrayItem(
                                              "education",
                                              index,
                                            )
                                          }
                                          size="small"
                                          color="primary"
                                          title={t("buttons.duplicateEducation")}
                                          disabled={loading || generatingPdf}
                                        >
                                          <ContentCopyIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                          onClick={() =>
                                            removeArrayItem(
                                              `education[${index}]`,
                                            )
                                          }
                                          size="small"
                                          color="error"
                                          title={t(
                                            "placeholders.removeEducation",
                                          )}
                                          disabled={loading || generatingPdf}
                                        >
                                          <RemoveCircleOutlineIcon fontSize="small" />
                                        </IconButton>
                                      </Box>
                                      <Stack spacing={1.5} sx={{ pl: 3 }}>
                                        {" "}
                                        {/* Indent content */}
                                        <TextField
                                          label={t("labels.degree")}
                                          value={edu.degree}
                                          onChange={(e) =>
                                            handleJsonChange(
                                              `education[${index}].degree`,
                                              e.target.value,
                                            )
                                          }
                                          variant="standard"
                                          fullWidth
                                          size="small"
                                          disabled={loading || generatingPdf}
                                        />
                                        <TextField
                                          label={t("labels.institution")}
                                          value={edu.institution}
                                          onChange={(e) =>
                                            handleJsonChange(
                                              `education[${index}].institution`,
                                              e.target.value,
                                            )
                                          }
                                          variant="standard"
                                          fullWidth
                                          size="small"
                                          disabled={loading || generatingPdf}
                                        />
                                        <TextField
                                          label={t("labels.dates")}
                                          value={edu.dates}
                                          onChange={(e) =>
                                            handleJsonChange(
                                              `education[${index}].dates`,
                                              e.target.value,
                                            )
                                          }
                                          variant="standard"
                                          fullWidth
                                          size="small"
                                          disabled={loading || generatingPdf}
                                        />
                                      </Stack>
                                    </Paper>
                                  )}
                                </Draggable>
                              ))}
                              {provided.placeholder}
                            </Stack>
                          </Box>
                        )}
                      </Droppable>
                    </Paper>

                    {/* Skills Section */}
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ mb: 1 }}
                      >
                        <Typography variant="subtitle1">
                          {t("labels.skills")}
                        </Typography>
                        <Button
                          startIcon={<AddCircleOutlineIcon />}
                          onClick={() =>
                            addArrayItem("skills", {
                              id: generateId(),
                              value: "",
                            })
                          }
                          size="small"
                          disabled={loading || generatingPdf}
                        >
                          {t("buttons.addSkill")}
                        </Button>
                      </Stack>
                      <Droppable droppableId="skills" type="skills">
                        {(provided) => (
                          <Box
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                          >
                            <Grid container spacing={1}>
                              {optimizedJson.skills.map((skill, index) => (
                                <Draggable
                                  key={skill.id}
                                  draggableId={skill.id}
                                  index={index}
                                >
                                  {(providedDrag) => (
                                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                      <Box
                                        ref={providedDrag.innerRef}
                                        {...providedDrag.draggableProps}
                                        sx={{ width: "100%" }}
                                      >
                                        <Box
                                          display="flex"
                                          alignItems="center"
                                          sx={{ width: "100%" }}
                                        >
                                          <Box
                                            {...providedDrag.dragHandleProps}
                                            sx={{
                                              opacity: 0.5,
                                              cursor: "grab",
                                              mr: 0.5,
                                              display: "flex",
                                              alignItems: "center",
                                            }}
                                          >
                                            <DragIndicatorIcon fontSize="small" />
                                          </Box>
                                          <TextField
                                            value={skill.value}
                                            onChange={(e) =>
                                              handleJsonChange(
                                                `skills[${index}].value`,
                                                e.target.value,
                                              )
                                            }
                                            variant="standard"
                                            fullWidth
                                            size="small"
                                            disabled={loading || generatingPdf}
                                          />
                                          <IconButton
                                            size="small"
                                            onClick={() =>
                                              duplicateArrayItem(
                                                "skills",
                                                index,
                                              )
                                            }
                                            disabled={loading || generatingPdf}
                                            aria-label={t("buttons.duplicateSkill")}
                                          >
                                            <ContentCopyIcon fontSize="small" />
                                          </IconButton>
                                          <IconButton
                                            size="small"
                                            onClick={() =>
                                              removeArrayItem(
                                                `skills[${index}]`,
                                              )
                                            }
                                            disabled={loading || generatingPdf}
                                            aria-label={t(
                                              "placeholders.removeSkill",
                                            )}
                                          >
                                            <RemoveCircleOutlineIcon fontSize="small" />
                                          </IconButton>
                                        </Box>
                                      </Box>
                                    </Grid>
                                  )}
                                </Draggable>
                              ))}
                              {provided.placeholder}
                            </Grid>
                          </Box>
                        )}
                      </Droppable>
                    </Paper>
                  </Stack>{" "}
                  {/* End Main Stack for Output */}
                </Paper>
              </Grid>
            )}
            {!optimizedJson && (
              <Grid size={{ xs: 12, md: 7 }}>
                <Paper className="glass-panel preview-panel" elevation={0}>
                  <Box className="preview-orbit">
                    <Box className="preview-resume-card">
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ mb: 2 }}
                      >
                        <AutoAwesomeIcon color="primary" />
                        <Typography variant="overline">
                          {t("outputPanel.liveAiOutput")}
                        </Typography>
                      </Stack>
                      <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
                        {t("outputPanel.heading")}
                      </Typography>
                      <Typography
                        color="text.secondary"
                        sx={{ mb: 3, maxWidth: 540 }}
                      >
                        {t("outputPanel.subheading")}
                      </Typography>
                      <Stack spacing={1.4}>
                        {[
                          t("outputPanel.feature1"),
                          t("outputPanel.feature2"),
                          t("outputPanel.feature3"),
                        ].map((item) => (
                          <Stack
                            key={item}
                            direction="row"
                            spacing={1.2}
                            alignItems="center"
                          >
                            <CheckCircleOutlineIcon
                              color="success"
                              fontSize="small"
                            />
                            <Typography>{item}</Typography>
                          </Stack>
                        ))}
                      </Stack>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            )}
            {/* End Conditional Output Grid Item */}
          </Grid>{" "}
          {/* End Main Grid Container */}
        </Container>
      </DragDropContext>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          textAlign: "center",
          py: 3,
          mt: 4,
          borderTop: "1px solid",
          borderColor: mode === "dark" ? "#334155" : "#E2E8F0",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          © {new Date().getFullYear()} my-aitools.online —{" "}
          <Box
            component="a"
            href="#/blog"
            sx={{
              color: "primary.main",
              textDecoration: "none",
              fontWeight: 600,
              "&:hover": { textDecoration: "underline" },
            }}
          >
            Blog
          </Box>
          {" · "}
          <Box
            component="a"
            href="#/about"
            sx={{
              color: "primary.main",
              textDecoration: "none",
              fontWeight: 600,
              "&:hover": { textDecoration: "underline" },
            }}
          >
            About
          </Box>
          {" · "}
          <Box
            component="a"
            href="/privacy-policy.html"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: "primary.main",
              textDecoration: "none",
              fontWeight: 600,
              "&:hover": { textDecoration: "underline" },
            }}
          >
            Privacy Policy
          </Box>
        </Typography>
      </Box>
    </>
  );
}

export default App;
